import React, { memo, useCallback, useMemo } from 'react'
import { ContainerDrawer } from '@/components/panels/ContainerDrawer'
import { PanelView } from '../PanelView'
import { ItemRecurrencePanelPart } from './ItemRecurrencePanelPart'
import { LocalizedDatePickerV9 } from '../../pickers/LocalizedDatePicker'
import { useDidChangeDate } from '../../../hooks/dateHooks'
import { useTranslation } from 'react-i18next'
import { Button, Field, Switch, Textarea } from '@fluentui/react-components'
import { reminderHelper } from '../../../common/src/helpers'
import { TimePickerCombo } from '../../timepicker/TimePickerCombo'
import { fromJS } from 'immutable'
import { dateToTimestampInSeconds } from '@/common/src/helpers/dateHelper'

const REMINDER_FREQUENCY_KEYS = [
  reminderHelper.FREQUENCY_TYPE.ONCE,
  reminderHelper.FREQUENCY_TYPE.WEEKDAYS,
  reminderHelper.FREQUENCY_TYPE.DAILY,
  reminderHelper.FREQUENCY_TYPE.WEEKLY,
  reminderHelper.FREQUENCY_TYPE.BIWEEKLY,
  reminderHelper.FREQUENCY_TYPE.MONTHLY,
  reminderHelper.FREQUENCY_TYPE.YEARLY,
]

const ReminderPanelView = memo(
  ({
    reminderDate,
    allFollowers,
    onChangeReminderDate,
    onToggleAllFollowers,
    reminderFrequency,
    onChangeReminderFrequency,
    reminderNotes,
    onChangeReminderNotes,
    reminderEnabled,
    onChangeReminderEnabled,
    setReminder,
    reminder,
    ...rest
  }) => {
    const onClearDate = useCallback(() => {
      onChangeReminderDate(0)
      onChangeReminderEnabled(false)
    }, [onChangeReminderDate, onChangeReminderEnabled])
    const { t } = useTranslation()

    const didChangeDateHandlerCreator = useDidChangeDate()

    const _handleChangeReminderDate = useCallback(
      value => {
        if (value) {
          onChangeReminderDate(value)
        } else {
          onClearDate()
        }
      },
      [onChangeReminderDate, onClearDate]
    )

    const handleChangeReminderDate = useMemo(() => {
      return didChangeDateHandlerCreator(reminderDate, _handleChangeReminderDate)
    }, [didChangeDateHandlerCreator, reminderDate, _handleChangeReminderDate])

    const onBlurNotes = useCallback(
      ev => {
        onChangeReminderNotes(ev.target.value)
      },
      [onChangeReminderNotes]
    )

    const onChangeAllFollowers = useCallback(
      (ev, data) => {
        onToggleAllFollowers(data.checked)
      },
      [onToggleAllFollowers]
    )

    const headerChildren = (
      <Button appearance="transparent" className="!min-w-0" disabled={!reminderEnabled} onClick={onClearDate}>
        {t('general.clear')}
      </Button>
    )
    return (
      <PanelView headerChildren={headerChildren} {...rest}>
        <div className="flex w-full flex-col items-start gap-2">
          <Button
            className="!min-w-0 !px-1.5 !py-0.5"
            appearance="secondary"
            onClick={() => {
              const date = new Date()
              date.setDate(date.getDate() + 1)
              const newReminder = reminder ?? fromJS({})
              setReminder(
                newReminder
                  .set(reminderHelper.KEYS.REMINDER_ENABLED, true)
                  .set(reminderHelper.KEYS.REMINDER_FREQUENCY, 2)
                  .set(reminderHelper.KEYS.REMINDER_ALL_FOLLOWERS, false)
                  .set(reminderHelper.KEYS.REMINDER_DATE, dateToTimestampInSeconds(date))
              )
            }}
          >
            {t('item.reminder.me_daily')}
          </Button>
          <Button
            className="!min-w-0 !px-1.5 !py-0.5"
            appearance="secondary"
            onClick={() => {
              // next weekday
              const next = new Date()
              // Add a day until we hit a weekday (1-5, Monday-Friday)
              do {
                next.setDate(next.getDate() + 1)
              } while (next.getDay() === 0 || next.getDay() === 6)
              const newReminder = reminder ?? fromJS({})
              setReminder(
                newReminder
                  .set(reminderHelper.KEYS.REMINDER_ENABLED, true)
                  .set(reminderHelper.KEYS.REMINDER_FREQUENCY, 7)
                  .set(reminderHelper.KEYS.REMINDER_ALL_FOLLOWERS, false)
                  .set(reminderHelper.KEYS.REMINDER_DATE, dateToTimestampInSeconds(next))
              )
            }}
          >
            {t('item.reminder.me_weekdays')}
          </Button>
          <Button
            className="!min-w-0 !px-1.5 !py-0.5"
            appearance="secondary"
            onClick={() => {
              const next = new Date()
              next.setMonth(next.getMonth() + 1)
              const newReminder = reminder ?? fromJS({})
              setReminder(
                newReminder
                  .set(reminderHelper.KEYS.REMINDER_ENABLED, true)
                  .set(reminderHelper.KEYS.REMINDER_FREQUENCY, 5)
                  .set(reminderHelper.KEYS.REMINDER_ALL_FOLLOWERS, true)
                  .set(reminderHelper.KEYS.REMINDER_DATE, dateToTimestampInSeconds(next))
              )
            }}
          >
            {t('item.reminder.everyone_monthly')}
          </Button>
        </div>
        <LocalizedDatePickerV9
          label={t('item.reminder.reminder_date')}
          value={reminderEnabled ? reminderDate : null}
          onSelectDate={handleChangeReminderDate}
        />
        {reminderDate && reminderEnabled && (
          <TimePickerCombo id="startTimePicker" value={reminderDate} onChange={onChangeReminderDate} />
        )}
        {!!onToggleAllFollowers && reminderDate && reminderEnabled && (
          <Switch label={t('item.reminder.all_followers')} checked={allFollowers} onChange={onChangeAllFollowers} />
        )}
        {reminderDate && reminderEnabled && (
          <ItemRecurrencePanelPart
            frequency={reminderFrequency}
            onChangeFrequency={onChangeReminderFrequency}
            frequencyKeys={REMINDER_FREQUENCY_KEYS}
            isReminder={true}
          />
        )}
        {reminderDate && reminderEnabled && (
          <Field label={t('item.notes')}>
            <Textarea
              id="itemReminder_notesTextField"
              onBlur={onBlurNotes}
              placeholder={t('item.reminder.notes_placeholder')}
              defaultValue={reminderNotes}
              textarea={{ className: '!min-h-20' }}
            />
          </Field>
        )}
      </PanelView>
    )
  }
)

export const ItemReminderPanel = memo(
  ({
    reminderDate,
    headerText,
    allFollowers,
    onChangeReminderDate,
    onToggleAllFollowers,
    reminderFrequency,
    onChangeReminderFrequency,
    onDismiss,
    reminderNotes,
    onChangeReminderNotes,
    reminderEnabled,
    onChangeReminderEnabled,
    setReminder,
    reminder,
    ...rest
  }) => {
    const { t } = useTranslation()
    const ht = headerText ?? t('item.reminder.edit_reminder')
    return (
      <ContainerDrawer headerText={ht} onDismiss={onDismiss} {...rest}>
        <ReminderPanelView
          reminderDate={reminderDate}
          allFollowers={allFollowers}
          onChangeReminderDate={onChangeReminderDate}
          onToggleAllFollowers={onToggleAllFollowers}
          reminderFrequency={reminderFrequency}
          onChangeReminderFrequency={onChangeReminderFrequency}
          reminderNotes={reminderNotes}
          onChangeReminderNotes={onChangeReminderNotes}
          reminderEnabled={reminderEnabled}
          onChangeReminderEnabled={onChangeReminderEnabled}
          setReminder={setReminder}
          reminder={reminder}
        />
      </ContainerDrawer>
    )
  }
)
